import { t } from "@lingui/core/macro";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { z } from "zod";

import AuthPage from "./-components/AuthPage";
import ConfirmSignIn from "./-components/ConfirmSignIn";
import NewPassword from "./-components/NewPassword";
import RecoveryCode from "./-components/RecoveryCode";
import ResetPassword from "./-components/ResetPassword";
import SignIn from "./-components/SignIn";

export const Route = createFileRoute("/_unauthenticated/sign-in")({
  component: SignInPage,
  validateSearch: z.object({
    challengeName: z.enum(["RESET_PASSWORD", "DISABLE_MFA", "NEW_PASSWORD_REQUIRED", "SOFTWARE_TOKEN_MFA"]).optional(),
    email: z.string().optional(),
  }),
});

export type ChallengedUser = {
  challengeName: "RESET_PASSWORD" | "DISABLE_MFA" | "NEW_PASSWORD_REQUIRED" | "SOFTWARE_TOKEN_MFA";
  email: string;
};

export default function SignInPage() {
  const { challengeName, email } = useSearch({ from: "/_unauthenticated/sign-in" });
  const navigate = useNavigate();
  const [challengedUser, setChallengedUserState] = useState<ChallengedUser | null>(
    challengeName && email ? { challengeName, email } : null,
  );

  const setChallengedUser = useCallback(
    (user: ChallengedUser | null) => {
      setChallengedUserState(user);
      if (challengeName !== undefined || email !== undefined) {
        void navigate({
          to: "/sign-in",
          search: { challengeName: undefined, email: undefined },
          replace: true,
        });
      }
    },
    [challengeName, email, navigate],
  );

  if (!challengedUser) {
    return <SignIn setChallengedUser={setChallengedUser} />;
  }

  if (challengedUser.challengeName === "DISABLE_MFA") {
    return <RecoveryCode email={challengedUser.email} setChallengedUser={setChallengedUser} />;
  }

  if (challengedUser.challengeName === "RESET_PASSWORD") {
    return <ResetPassword email={challengedUser.email} setChallengedUser={setChallengedUser} />;
  }
  if (challengedUser.challengeName === "NEW_PASSWORD_REQUIRED") {
    return <NewPassword challengedUser={challengedUser} setChallengedUser={setChallengedUser} />;
  }
  if (challengedUser.challengeName === "SOFTWARE_TOKEN_MFA") {
    return <ConfirmSignIn challengedUser={challengedUser} setChallengedUser={setChallengedUser} />;
  }

  return (
    <AuthPage>
      <p>{t`We were unable to sign you in to your account. Please try again later or reach out to support if the issue persists.`}</p>
    </AuthPage>
  );
}
